import type { Category, ProductLine } from '~/types'

// Cache for category data (longer TTL since it changes less frequently)
interface CategoriesCache {
  categories: Category[] | null
  productLines: ProductLine[] | null
  componentTypes: string[] | null
  timestamp: number
}

const cache: CategoriesCache = {
  categories: null,
  productLines: null,
  componentTypes: null,
  timestamp: 0
}

const CACHE_TTL = 15 * 60 * 1000 // 15 minutes

export function useCategories() {
  const supabase = useSupabaseClient()

  // Check if cache is valid
  const isCacheValid = (): boolean => {
    return Date.now() - cache.timestamp < CACHE_TTL
  }

  // Fetch all categories with caching
  const fetchCategories = async (): Promise<Category[]> => {
    if (cache.categories && isCacheValid()) {
      return cache.categories
    }

    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order')
        .order('name')

      if (error) {
        console.error('Error fetching categories:', error)
        throw error
      }

      cache.categories = (data as Category[]) || []
      cache.timestamp = Date.now()
      return cache.categories
    } catch (error) {
      console.error('Error in fetchCategories:', error)
      // Return cached data if available, even if stale
      return cache.categories || []
    }
  }

  // Fetch all product lines with caching
  const fetchProductLines = async (): Promise<ProductLine[]> => {
    if (cache.productLines && isCacheValid()) {
      return cache.productLines
    }

    try {
      const { data, error } = await supabase
        .from('product_lines')
        .select('*')
        .order('sort_order')
        .order('name')

      if (error) {
        console.error('Error fetching product lines:', error)
        throw error
      }

      cache.productLines = (data as ProductLine[]) || []
      cache.timestamp = Date.now()
      return cache.productLines
    } catch (error) {
      console.error('Error in fetchProductLines:', error)
      // Return cached data if available, even if stale
      return cache.productLines || []
    }
  }

  // Fetch unique component types from products with caching
  const fetchComponentTypes = async (): Promise<string[]> => {
    if (cache.componentTypes && isCacheValid()) {
      return cache.componentTypes
    }

    try {
      const { data, error } = await supabase
        .from('products')
        .select('component_type')
        .eq('is_active', true)
        .not('component_type', 'is', null)

      if (error) {
        console.error('Error fetching component types:', error)
        throw error
      }

      // Extract unique values
      const types = new Set<string>()
      data?.forEach((row: { component_type: string | null }) => {
        if (row.component_type) {
          types.add(row.component_type)
        }
      })

      cache.componentTypes = Array.from(types).sort()
      cache.timestamp = Date.now()
      return cache.componentTypes
    } catch (error) {
      console.error('Error in fetchComponentTypes:', error)
      // Return cached data if available, even if stale
      return cache.componentTypes || []
    }
  }

  // Clear cache (useful for admin updates)
  const clearCache = () => {
    cache.categories = null
    cache.productLines = null
    cache.componentTypes = null
    cache.timestamp = 0
  }

  return {
    fetchCategories,
    fetchProductLines,
    fetchComponentTypes,
    clearCache
  }
}
